"use client";

import { AccountingBenefits } from "../AccountingBenefits";
import { AccountingPlanPicker } from "../AccountingPlanPicker";
import { DidYouKnowDisclosure } from "../DidYouKnowDisclosure";
import { HkAccountantNote } from "../HkAccountantNote";
import { MonthlyExpensesSelect } from "../MonthlyExpensesSelect";
import { OrderSummaryCard } from "../OrderSummaryCard";
import { MANAGE_ACCOUNTING_PLANS, type PackageSummary } from "../pricing";

/**
 * The manage branch's accounting screen, reached from a "no" on the
 * past-returns step.
 *
 * Same heading block, benefits, HK accountant note and expenses picker as
 * AccountingSupportStep, with two differences from the register branch: the
 * tiers appear only once an expense band is chosen (the design shows the
 * picker's placeholder state with no cards, and the chosen state with them),
 * and there is no founder tip.
 */
export function ManageAccountingStep({
  monthlyExpenses,
  onMonthlyExpensesChange,
  accountingPlanId,
  onAccountingPlanChange,
  summary,
  onNext,
  onSkip,
}: {
  monthlyExpenses: string;
  onMonthlyExpensesChange: (value: string) => void;
  accountingPlanId: string | null;
  onAccountingPlanChange: (id: string) => void;
  summary: PackageSummary;
  onNext: () => void;
  onSkip: () => void;
}) {
  const bandChosen = monthlyExpenses.length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (accountingPlanId) onNext();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col items-start gap-6 sm:gap-8">
          <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
            <h1 className="font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[54px] lg:leading-[71px]">
              Compliance-Ready Accounting from Day One
            </h1>
            <p className="font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
              Keeping your books accurate from day one helps you stay compliant and avoid costly
              clean-up work later.
            </p>
          </div>

          <AccountingBenefits />

          <HkAccountantNote />

          <hr className="w-full border-t border-[#283143]" />

          <div className="flex w-full flex-col items-start gap-[10px]">
            <h2 className="font-inter text-base font-bold text-[#F3F4F6] sm:text-lg">
              Choose Your Monthly Expenses – Get the Right Plan for Your Business
            </h2>
            <p className="font-inter text-sm text-[#9CA3AF]">
              Your plan is based on your monthly expenses, if unsure start with lowest and adjust
              later.
            </p>
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <MonthlyExpensesSelect value={monthlyExpenses} onChange={onMonthlyExpensesChange} />
            <DidYouKnowDisclosure />
          </div>

          {/* The tiers are priced off the expense band, so they stay out of the
              way until one is picked — showing three prices before the question
              that determines them invites choosing the wrong tier. */}
          {bandChosen && (
            <AccountingPlanPicker
              plans={MANAGE_ACCOUNTING_PLANS}
              selectedId={accountingPlanId}
              onSelect={onAccountingPlanChange}
              // The design tints this screen's ticks blue, unlike the register
              // branch's white ones.
              tickClassName="text-[#38BDF8]"
            />
          )}

          {/* col-reverse on phones so the primary action sits above "Skip this"
              despite coming second in the DOM. */}
          <div className="flex w-full flex-col-reverse items-stretch gap-3 pt-4 sm:flex-row sm:items-center sm:justify-end sm:gap-4 sm:pt-6">
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-3 font-inter text-sm font-bold text-white transition-opacity hover:opacity-80"
            >
              Skip this
            </button>
            <button
              type="submit"
              disabled={!accountingPlanId}
              className="flex h-14 w-full items-center justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 sm:w-[120px]"
            >
              Next
            </button>
          </div>
        </div>

        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
